import apiClient from "./apiClient";
import type {
  GeneratedCodeEntity,
  CreateGeneratedCodePayload,
  UpdateGeneratedCodePayload,
  GeneratedCodeFilters,
  GeneratedCodeStats,
  AxiosErrorResponse,
} from "@/types/generatedCode";
import type { SilaeFormData } from "@/types/silae";

/**
 * Generated Code Service
 * Service sécurisé et optimisé pour gérer l'historique des codes SILAE générés
 */

// ============================================================================
// CONSTANTS
// ============================================================================

const API_ENDPOINTS = {
  BASE: "/api/generated-codes",
  TEMPLATES: "/api/generated-codes/templates",
  STATS: "/api/generated-codes/stats",
  GENERATE_PREVIEW: "/api/generated-codes/generate-preview",
  BY_ID: (id: string) => `/api/generated-codes/${id}`,
  COMMENT: (id: string) => `/api/generated-codes/${id}/comment`,
} as const;

// ============================================================================
// VALIDATION HELPERS
// ============================================================================

/**
 * Valide les données d'entrée pour la création de code
 */
const COMMENT_MAX_LENGTH = 1500;

const validateCreateInput = (input: CreateGeneratedCodePayload): void => {
  if (!input.name?.trim()) {
    throw new Error("Le nom du code est requis");
  }
  if (input.name.length > 255) {
    throw new Error("Le nom du code ne doit pas dépasser 255 caractères");
  }
  if (!input.formData) {
    throw new Error("Les données du formulaire sont requises");
  }
  if (input.tags && !Array.isArray(input.tags)) {
    throw new Error("Les tags doivent être un tableau");
  }
  if (input.tags && input.tags.length > 50) {
    throw new Error("Le nombre maximum de tags est de 50");
  }
  if (
    typeof input.comment === "string" &&
    input.comment.length > COMMENT_MAX_LENGTH
  ) {
    throw new Error(
      `Le commentaire ne doit pas dépasser ${COMMENT_MAX_LENGTH} caractères`,
    );
  }
};

/**
 * Valide les données d'entrée pour la mise à jour de code
 */
const validateUpdateInput = (input: UpdateGeneratedCodePayload): void => {
  if (input.name !== undefined && !input.name.trim()) {
    throw new Error("Le nom du code ne peut pas être vide");
  }
  if (input.name && input.name.length > 255) {
    throw new Error("Le nom du code ne doit pas dépasser 255 caractères");
  }
  if (input.tags && !Array.isArray(input.tags)) {
    throw new Error("Les tags doivent être un tableau");
  }
  if (input.tags && input.tags.length > 50) {
    throw new Error("Le nombre maximum de tags est de 50");
  }
  if (
    typeof input.comment === "string" &&
    input.comment.length > COMMENT_MAX_LENGTH
  ) {
    throw new Error(
      `Le commentaire ne doit pas dépasser ${COMMENT_MAX_LENGTH} caractères`,
    );
  }
};

/**
 * Nettoie les données d'entrée
 */
const sanitizeInput = <
  T extends CreateGeneratedCodePayload | UpdateGeneratedCodePayload,
>(
  input: T,
): T => {
  const sanitized = { ...input };

  // Trim les chaînes de caractères
  if (sanitized.name) {
    sanitized.name = sanitized.name.trim();
  }
  if (sanitized.description) {
    sanitized.description = sanitized.description.trim();
  }
  if (typeof sanitized.comment === "string") {
    const trimmed = sanitized.comment.trim();
    sanitized.comment = trimmed.length === 0 ? null : trimmed;
  }

  // Nettoie les tags
  if (sanitized.tags && Array.isArray(sanitized.tags)) {
    sanitized.tags = sanitized.tags
      .filter(
        (tag): tag is string =>
          typeof tag === "string" && tag.trim().length > 0,
      )
      .map((tag) => tag.trim());
  }

  return sanitized;
};

/**
 * Construit l'URL avec les paramètres de filtrage
 */
const buildFilterUrl = (filters?: GeneratedCodeFilters): string => {
  if (!filters) return API_ENDPOINTS.BASE;

  const params = new URLSearchParams();

  if (filters.isTemplate !== undefined) {
    params.append("isTemplate", String(filters.isTemplate));
  }

  if (filters.structureType?.trim()) {
    params.append("structureType", filters.structureType.trim());
  }

  if (filters.tags?.length) {
    params.append("tags", filters.tags.join(","));
  }

  if (filters.rubriques?.length) {
    params.append("rubriques", filters.rubriques.join(","));
  }

  if (filters.search?.trim()) {
    params.append("search", filters.search.trim());
  }

  const queryString = params.toString();
  return queryString
    ? `${API_ENDPOINTS.BASE}?${queryString}`
    : API_ENDPOINTS.BASE;
};

// ============================================================================
// ERROR HANDLING HELPERS
// ============================================================================

/**
 * Extrait un message d'erreur lisible depuis une erreur API
 */
const extractErrorMessage = (error: unknown): string => {
  // Si c'est déjà une Error avec un message, on le retourne
  if (error instanceof Error) {
    return error.message;
  }

  // Si c'est une erreur Axios avec une réponse
  if (error && typeof error === "object" && "response" in error) {
    const axiosError = error as AxiosErrorResponse;

    // Message personnalisé du serveur
    if (axiosError.response?.data?.message) {
      return axiosError.response.data.message;
    }

    // Message d'erreur alternatif
    if (axiosError.response?.data?.error) {
      return axiosError.response.data.error;
    }

    // Message basé sur le status HTTP
    if (axiosError.response?.status) {
      const status = axiosError.response.status;
      const statusText = axiosError.response.statusText || "";

      switch (status) {
        case 400:
          return "Requête invalide. Veuillez vérifier les données envoyées.";
        case 401:
          return "Non autorisé. Veuillez vous reconnecter.";
        case 403:
          return "Accès refusé. Vous n'avez pas les permissions nécessaires.";
        case 404:
          return "Ressource introuvable.";
        case 409:
          return "Conflit. Cette ressource existe déjà.";
        case 422:
          return "Données invalides. Veuillez vérifier les informations saisies.";
        case 500:
          return "Erreur serveur. Veuillez réessayer plus tard.";
        case 503:
          return "Service indisponible. Veuillez réessayer plus tard.";
        default:
          return `Erreur ${status}${statusText ? `: ${statusText}` : ""}`;
      }
    }

    // Message de l'erreur Axios si disponible
    if (axiosError.message) {
      return axiosError.message;
    }
  }

  // Erreur avec propriété message
  if (
    error &&
    typeof error === "object" &&
    "message" in error &&
    typeof (error as { message: string }).message === "string"
  ) {
    return (error as { message: string }).message;
  }

  // Message par défaut
  return "Une erreur inattendue s'est produite. Veuillez réessayer.";
};

/**
 * Log une erreur pour le debugging (en développement uniquement)
 */
const logError = (context: string, error: unknown): void => {
  if (import.meta.env.NODE_ENV === "development") {
    console.error(`[GeneratedCodeService] ${context}:`, error);
  }
};

// ============================================================================
// API FUNCTIONS
// ============================================================================

/**
 * Crée un nouveau code généré
 * @throws {Error} Si la validation échoue ou si la requête échoue
 */
export const createGeneratedCode = async (
  input: CreateGeneratedCodePayload,
): Promise<GeneratedCodeEntity> => {
  try {
    // Validation
    validateCreateInput(input);
    const sanitizedInput = sanitizeInput(input);

    // Appel API
    const response = await apiClient.post(API_ENDPOINTS.BASE, sanitizedInput);

    if (!response?.data?.data) {
      throw new Error("Réponse invalide du serveur");
    }

    return response.data.data;
  } catch (error) {
    logError("createGeneratedCode", error);

    // Si c'est une erreur de validation, on la propage telle quelle
    if (
      error instanceof Error &&
      (error.message.includes("requis") ||
        error.message.includes("dépasser") ||
        error.message.includes("maximum"))
    ) {
      throw error;
    }

    // Sinon, on crée une nouvelle erreur avec un message plus clair
    const message = extractErrorMessage(error);
    throw new Error(`Échec de la création du code: ${message}`);
  }
};

/**
 * Récupère tous les codes générés pour l'utilisateur actuel avec filtres optionnels
 * @throws {Error} Si la requête échoue
 */
export const getGeneratedCodes = async (
  filters?: GeneratedCodeFilters,
): Promise<GeneratedCodeEntity[]> => {
  try {
    const url = buildFilterUrl(filters);
    const response = await apiClient.get(url);

    if (!response?.data?.data) {
      throw new Error("Réponse invalide du serveur");
    }

    // S'assurer que c'est un tableau
    if (!Array.isArray(response.data.data)) {
      throw new Error("Format de réponse invalide: un tableau était attendu");
    }

    return response.data.data;
  } catch (error) {
    logError("getGeneratedCodes", error);
    const message = extractErrorMessage(error);
    throw new Error(`Échec du chargement des codes: ${message}`);
  }
};

/**
 * Récupère uniquement les templates
 * @throws {Error} Si la requête échoue
 */
export const getTemplates = async (): Promise<GeneratedCodeEntity[]> => {
  try {
    const response = await apiClient.get(API_ENDPOINTS.TEMPLATES);

    if (!response?.data?.data) {
      throw new Error("Réponse invalide du serveur");
    }

    // S'assurer que c'est un tableau
    if (!Array.isArray(response.data.data)) {
      throw new Error("Format de réponse invalide: un tableau était attendu");
    }

    return response.data.data;
  } catch (error) {
    logError("getTemplates", error);
    const message = extractErrorMessage(error);
    throw new Error(`Échec du chargement des templates: ${message}`);
  }
};

/**
 * Récupère les statistiques des codes générés de l'utilisateur
 * @throws {Error} Si la requête échoue
 */
export const getGeneratedCodeStats = async (): Promise<GeneratedCodeStats> => {
  try {
    const response = await apiClient.get(API_ENDPOINTS.STATS);

    if (!response?.data?.data) {
      throw new Error("Réponse invalide du serveur");
    }

    return response.data.data;
  } catch (error) {
    logError("getGeneratedCodeStats", error);
    const message = extractErrorMessage(error);
    throw new Error(`Échec du chargement des statistiques: ${message}`);
  }
};

/**
 * Récupère un code généré spécifique par son ID
 * @throws {Error} Si l'ID est invalide ou si la requête échoue
 */
export const getGeneratedCodeById = async (
  id: string,
): Promise<GeneratedCodeEntity> => {
  try {
    // Validation de l'ID
    if (!id?.trim()) {
      throw new Error("L'ID du code est requis");
    }

    const response = await apiClient.get(API_ENDPOINTS.BY_ID(id.trim()));

    if (!response?.data?.data) {
      throw new Error("Code introuvable ou réponse invalide du serveur");
    }

    return response.data.data;
  } catch (error) {
    logError("getGeneratedCodeById", error);

    // Si c'est une erreur de validation, on la propage telle quelle
    if (error instanceof Error && error.message.includes("requis")) {
      throw error;
    }

    const message = extractErrorMessage(error);
    throw new Error(`Échec du chargement du code: ${message}`);
  }
};

/**
 * Met à jour un code généré existant
 * @throws {Error} Si l'ID est invalide, la validation échoue ou la requête échoue
 */
export const updateGeneratedCode = async (
  id: string,
  input: UpdateGeneratedCodePayload,
): Promise<GeneratedCodeEntity> => {
  try {
    // Validation de l'ID
    if (!id?.trim()) {
      throw new Error("L'ID du code est requis");
    }

    // Validation des données
    validateUpdateInput(input);
    const sanitizedInput = sanitizeInput(input);

    // Appel API
    const response = await apiClient.put(
      API_ENDPOINTS.BY_ID(id.trim()),
      sanitizedInput,
    );

    if (!response?.data?.data) {
      throw new Error("Réponse invalide du serveur");
    }

    return response.data.data;
  } catch (error) {
    logError("updateGeneratedCode", error);

    // Si c'est une erreur de validation, on la propage telle quelle
    if (
      error instanceof Error &&
      (error.message.includes("requis") ||
        error.message.includes("dépasser") ||
        error.message.includes("maximum") ||
        error.message.includes("vide"))
    ) {
      throw error;
    }

    const message = extractErrorMessage(error);
    throw new Error(`Échec de la mise à jour du code: ${message}`);
  }
};

/**
 * Met à jour uniquement le commentaire d'un code généré
 * @throws {Error} Si l'ID est invalide ou si la requête échoue
 */
export const updateGeneratedCodeComment = async (
  id: string,
  comment: string | null,
): Promise<GeneratedCodeEntity> => {
  try {
    if (!id?.trim()) {
      throw new Error("L'ID du code est requis");
    }
    if (typeof comment === "string" && comment.length > COMMENT_MAX_LENGTH) {
      throw new Error(
        `Le commentaire ne doit pas dépasser ${COMMENT_MAX_LENGTH} caractères`,
      );
    }
    const response = await apiClient.patch(API_ENDPOINTS.COMMENT(id.trim()), {
      comment:
        typeof comment === "string" && comment.trim().length > 0
          ? comment.trim()
          : null,
    });
    if (!response?.data?.data) {
      throw new Error("Réponse invalide du serveur");
    }
    return response.data.data;
  } catch (error) {
    logError("updateGeneratedCodeComment", error);
    if (
      error instanceof Error &&
      (error.message.includes("requis") || error.message.includes("dépasser"))
    ) {
      throw error;
    }
    const message = extractErrorMessage(error);
    throw new Error(`Échec de la sauvegarde du commentaire: ${message}`);
  }
};

/**
 * Supprime un code généré
 * @throws {Error} Si l'ID est invalide ou si la requête échoue
 */
export const deleteGeneratedCode = async (id: string): Promise<void> => {
  try {
    // Validation de l'ID
    if (!id?.trim()) {
      throw new Error("L'ID du code est requis");
    }

    // Appel API
    await apiClient.delete(API_ENDPOINTS.BY_ID(id.trim()));
  } catch (error) {
    logError("deleteGeneratedCode", error);

    // Si c'est une erreur de validation, on la propage telle quelle
    if (error instanceof Error && error.message.includes("requis")) {
      throw error;
    }

    const message = extractErrorMessage(error);
    throw new Error(`Échec de la suppression du code: ${message}`);
  }
};

/**
 * Génère un aperçu du code SILAE sans persister en base de données.
 * Appelle le backend POST /api/generated-codes/generate-preview.
 * @throws {Error} Si la validation échoue ou si la requête échoue
 */
export const generateCodePreview = async (
  formData: SilaeFormData,
): Promise<string> => {
  try {
    if (!formData) {
      throw new Error("Les données du formulaire sont requises");
    }

    const response = await apiClient.post(API_ENDPOINTS.GENERATE_PREVIEW, {
      formData,
    });

    if (!response?.data?.data?.generatedCode) {
      throw new Error("Réponse invalide du serveur");
    }

    return response.data.data.generatedCode as string;
  } catch (error) {
    logError("generateCodePreview", error);

    if (error instanceof Error && error.message.includes("requises")) {
      throw error;
    }

    const message = extractErrorMessage(error);
    throw new Error(`Échec de la génération du code: ${message}`);
  }
};

// Re-export types for convenience
export type {
  GeneratedCodeEntity as GeneratedCode,
  CreateGeneratedCodePayload as CreateGeneratedCodeInput,
  UpdateGeneratedCodePayload as UpdateGeneratedCodeInput,
  GeneratedCodeFilters as GetGeneratedCodesFilters,
};
